'use client';

import { useState, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, Radar, Legend, Cell,
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────
interface MarketValidationData {
  label: string;
  state: string;
  medianYearBuilt: number;
  housingUnits: number;
  medianHomeValue: number;
  population: number;
  estimatedHomeAge: number;
  roofingDemandScore: number;
  housingAgeCategory: 'High Demand' | 'Moderate Demand' | 'Low Demand';
  housingAgeCategoryColor: string;
  hailEventsCount: number;
  windEventsCount: number;
  totalStormEvents: number;
  stormRiskLevel: 'Very High' | 'High' | 'Moderate' | 'Low';
  stormRiskColor: string;
  validationStatus: 'Confirmed' | 'Overestimated' | 'Underestimated' | 'Unverified';
  validationColor: string;
  dataSource: string;
}

interface ValidationResponse {
  success: boolean;
  data: MarketValidationData[];
  fetchedAt: string;
  sources: string[];
  error?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatK(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function getScoreGradient(score: number): string {
  if (score >= 75) return 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
  if (score >= 50) return 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)';
  return 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)';
}

// ─── Score Ring ───────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 56 }: { score: number; size?: number }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (score / 100) * circumference;
  const color = score >= 75 ? '#10B981' : score >= 50 ? '#F59E0B' : '#6B7280';

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E5E7EB" strokeWidth={5} />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth={5}
          strokeDasharray={`${filled} ${circumference - filled}`}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute text-xs font-black" style={{ color }}>{score}</span>
    </div>
  );
}

// ─── Validation Badge ─────────────────────────────────────────────────────────
function ValidationBadge({ status, color }: { status: string; color: string }) {
  const icons: Record<string, string> = {
    Confirmed: 'CheckCircleIcon',
    Overestimated: 'ArrowTrendingDownIcon',
    Underestimated: 'ArrowTrendingUpIcon',
    Unverified: 'QuestionMarkCircleIcon',
  };
  return (
    <div
      className="flex items-center gap-1 px-2 py-0.5 rounded-full"
      style={{ background: `${color}18`, border: `1px solid ${color}40` }}
    >
      <Icon name={icons[status] as any} size={10} style={{ color }} />
      <span className="text-[10px] font-bold" style={{ color }}>{status}</span>
    </div>
  );
}

// ─── City Row ─────────────────────────────────────────────────────────────────
function CityValidationRow({ item, isSelected, onClick }: {
  item: MarketValidationData;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-3 transition-all hover:bg-blue-50 border-b last:border-b-0"
      style={{
        borderColor: '#F3F4F6',
        background: isSelected ? 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)' : 'transparent',
        borderLeft: isSelected ? '3px solid #E89A3C' : '3px solid transparent',
      }}
    >
      <div className="flex items-center gap-3">
        <ScoreRing score={item.roofingDemandScore} size={44} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold truncate" style={{ color: '#1C2E5E' }}>{item.label}</span>
            <ValidationBadge status={item.validationStatus} color={item.validationColor} />
          </div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="text-[10px]" style={{ color: '#6B7280' }}>
              🏠 {item.estimatedHomeAge}yr avg age
            </span>
            <span className="text-[10px]" style={{ color: item.stormRiskColor }}>
              ⛈ {item.totalStormEvents} storm events
            </span>
            <span className="text-[10px]" style={{ color: item.housingAgeCategoryColor }}>
              {item.housingAgeCategory}
            </span>
          </div>
        </div>
        <Icon name="ChevronRightIcon" size={14} style={{ color: '#9CA3AF', flexShrink: 0 }} />
      </div>
    </button>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────
function CityDetailPanel({ item }: { item: MarketValidationData }) {
  const radarData = [
    { subject: 'Home Age', value: Math.min(100, item.estimatedHomeAge * 2.5) },
    { subject: 'Storm Risk', value: Math.min(100, item.totalStormEvents * 3.5) },
    { subject: 'Housing Vol.', value: Math.min(100, (item.housingUnits / 500000) * 100) },
    { subject: 'Home Value', value: Math.min(100, (item.medianHomeValue / 600000) * 100) },
    { subject: 'Demand Score', value: item.roofingDemandScore },
  ];

  const stormBreakdown = [
    { name: 'Hail Events', value: item.hailEventsCount, fill: '#EF4444' },
    { name: 'Wind/Tornado', value: item.windEventsCount, fill: '#F97316' },
  ];

  const metrics = [
    {
      label: 'Median Year Built',
      value: String(item.medianYearBuilt),
      sub: `~${item.estimatedHomeAge} years old`,
      icon: 'HomeIcon',
      color: item.housingAgeCategoryColor,
      bg: `${item.housingAgeCategoryColor}12`,
    },
    {
      label: 'Total Housing Units',
      value: formatK(item.housingUnits),
      sub: 'Census ACS 2022',
      icon: 'BuildingOffice2Icon',
      color: '#E89A3C',
      bg: '#EFF6FF',
    },
    {
      label: 'Median Home Value',
      value: `$${formatK(item.medianHomeValue)}`,
      sub: 'Census ACS 2022',
      icon: 'CurrencyDollarIcon',
      color: '#8B5CF6',
      bg: '#F5F3FF',
    },
    {
      label: 'Storm Events (5yr)',
      value: String(item.totalStormEvents),
      sub: `${item.hailEventsCount} hail · ${item.windEventsCount} wind`,
      icon: 'BoltIcon',
      color: item.stormRiskColor,
      bg: `${item.stormRiskColor}12`,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div
        className="rounded-xl p-4"
        style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #1C2E5E 100%)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-bold text-white">{item.label}</h4>
            <p className="text-xs text-blue-200 mt-0.5">Roofing Demand Validation</p>
          </div>
          <div className="text-center">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ background: getScoreGradient(item.roofingDemandScore) }}
            >
              <span className="text-lg font-black text-white">{item.roofingDemandScore}</span>
            </div>
            <span className="text-[10px] text-blue-200 mt-1 block">Demand Score</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ValidationBadge status={item.validationStatus} color={item.validationColor} />
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: `${item.stormRiskColor}25`, color: item.stormRiskColor, border: `1px solid ${item.stormRiskColor}40` }}
          >
            ⛈ {item.stormRiskLevel} Storm Risk
          </span>
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: `${item.housingAgeCategoryColor}25`, color: item.housingAgeCategoryColor, border: `1px solid ${item.housingAgeCategoryColor}40` }}
          >
            🏠 {item.housingAgeCategory}
          </span>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 gap-2">
        {metrics.map((m) => (
          <div
            key={m.label}
            className="rounded-xl p-3"
            style={{ background: m.bg, border: `1px solid ${m.color}25` }}
          >
            <div className="flex items-center gap-1.5 mb-1.5">
              <div
                className="w-5 h-5 rounded-lg flex items-center justify-center"
                style={{ background: m.color }}
              >
                <Icon name={m.icon as any} size={10} className="text-white" />
              </div>
              <span className="text-[10px] font-semibold" style={{ color: '#6B7280' }}>{m.label}</span>
            </div>
            <div className="text-base font-black" style={{ color: '#1C2E5E' }}>{m.value}</div>
            <div className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>{m.sub}</div>
          </div>
        ))}
      </div>

      {/* Radar Chart */}
      <div className="rounded-xl p-3" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
        <div className="flex items-center gap-2 mb-2">
          <Icon name="ChartBarIcon" size={12} style={{ color: '#1C2E5E' }} />
          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>
            Market Signal Radar
          </span>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <RadarChart data={radarData}>
            <PolarGrid stroke="#E5E7EB" />
            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 9, fill: '#6B7280' }} />
            <Radar
              name="Score"
              dataKey="value"
              stroke="#E89A3C"
              fill="#E89A3C"
              fillOpacity={0.25}
              strokeWidth={2}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Storm Breakdown */}
      {item.totalStormEvents > 0 && (
        <div className="rounded-xl p-3" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="BoltIcon" size={12} style={{ color: '#EF4444' }} />
            <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#991B1B' }}>
              Storm Event Breakdown (5yr)
            </span>
          </div>
          <ResponsiveContainer width="100%" height={80}>
            <BarChart data={stormBreakdown} layout="vertical" margin={{ left: 0, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#FEE2E2" />
              <XAxis type="number" tick={{ fontSize: 9, fill: '#9CA3AF' }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#6B7280' }} width={70} />
              <Tooltip
                contentStyle={{ fontSize: 10, borderRadius: 8, border: '1px solid #FECACA' }}
                formatter={(v: any) => [`${v} events`, '']}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {stormBreakdown.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Data Source */}
      <div
        className="rounded-xl p-3 flex items-start gap-2"
        style={{ background: '#F0FDF4', border: '1px solid #A7F3D0' }}
      >
        <Icon name="CheckBadgeIcon" size={14} style={{ color: '#059669', flexShrink: 0, marginTop: 1 }} />
        <div>
          <p className="text-[10px] font-bold" style={{ color: '#065F46' }}>Verified Data Sources</p>
          <p className="text-[10px] mt-0.5 leading-relaxed" style={{ color: '#047857' }}>{item.dataSource}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Overview Bar Chart ───────────────────────────────────────────────────────
function DemandOverviewChart({ data }: { data: MarketValidationData[] }) {
  const chartData = data.slice(0, 10).map((d) => ({
    city: d.label.split(',')[0],
    demand: d.roofingDemandScore,
    homeAge: Math.min(100, d.estimatedHomeAge * 2),
    stormRisk: Math.min(100, d.totalStormEvents * 3),
    fill: d.roofingDemandScore >= 75 ? '#10B981' : d.roofingDemandScore >= 50 ? '#F59E0B' : '#6B7280',
  }));

  return (
    <div className="rounded-xl p-4" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-6 h-6 rounded-lg flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}
        >
          <Icon name="ChartBarIcon" size={12} className="text-white" />
        </div>
        <span className="text-xs font-bold" style={{ color: '#1C2E5E' }}>
          Roofing Demand Score by Market
        </span>
        <span className="text-[10px] ml-auto px-2 py-0.5 rounded-full" style={{ background: '#E0F2FE', color: '#0369A1' }}>
          Census + NOAA Validated
        </span>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="city"
            tick={{ fontSize: 9, fill: '#6B7280' }}
            angle={-35}
            textAnchor="end"
            interval={0}
          />
          <YAxis tick={{ fontSize: 9, fill: '#9CA3AF' }} domain={[0, 100]} />
          <Tooltip
            contentStyle={{ fontSize: 10, borderRadius: 8, border: '1px solid #E5E7EB' }}
            formatter={(v: any) => [`${v}/100`, 'Demand Score']}
          />
          <Bar dataKey="demand" radius={[4, 4, 0, 0]}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-4 mt-2 justify-center">
        {[
          { color: '#10B981', label: 'High Demand (75+)' },
          { color: '#F59E0B', label: 'Moderate (50-74)' },
          { color: '#6B7280', label: 'Low (<50)' },
        ].map((l) => (
          <div key={l.label} className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ background: l.color }} />
            <span className="text-[9px]" style={{ color: '#6B7280' }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MarketValidationPanel() {
  const [data, setData] = useState<MarketValidationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<MarketValidationData | null>(null);
  const [sources, setSources] = useState<string[]>([]);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchValidationData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/expansion/market-validation');
      const json: ValidationResponse = await res.json();
      if (!json.success) throw new Error(json.error ?? 'Failed to fetch validation data');
      setData(json.data);
      setSources(json.sources ?? []);
      setFetchedAt(json.fetchedAt);
      if (json.data.length > 0) setSelectedCity(json.data[0]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Lazy load on expand
  const handleExpand = useCallback(() => {
    if (!hasFetched) {
      setHasFetched(true);
      fetchValidationData();
    }
  }, [hasFetched, fetchValidationData]);

  const [isExpanded, setIsExpanded] = useState(false);

  const toggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    if (next) handleExpand();
  };

  // Summary stats
  const highDemandCount = data.filter(d => d.roofingDemandScore >= 75).length;
  const avgDemandScore = data.length > 0 ? Math.round(data.reduce((s, d) => s + d.roofingDemandScore, 0) / data.length) : 0;
  const highStormCount = data.filter(d => d.stormRiskLevel === 'Very High' || d.stormRiskLevel === 'High').length;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ border: '1px solid #E2E8F0', background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}
    >
      {/* Header */}
      <button
        onClick={toggle}
        className="w-full flex items-center gap-3 px-5 py-4 transition-colors hover:bg-gray-50"
        style={{ borderBottom: isExpanded ? '1px solid #E2E8F0' : 'none' }}
      >
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}
        >
          <Icon name="ShieldCheckIcon" size={18} className="text-white" />
        </div>
        <div className="flex-1 text-left">
          <h3 className="text-sm font-bold" style={{ color: '#1C2E5E' }}>
            AI Prediction Validator
          </h3>
          <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
            Census Bureau housing age · NOAA storm history · Roofing demand cross-validation
          </p>
        </div>
        {data.length > 0 && (
          <div className="flex items-center gap-3 mr-2">
            <div className="text-center">
              <div className="text-sm font-black" style={{ color: '#10B981' }}>{highDemandCount}</div>
              <div className="text-[9px]" style={{ color: '#9CA3AF' }}>High Demand</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-black" style={{ color: '#F59E0B' }}>{avgDemandScore}</div>
              <div className="text-[9px]" style={{ color: '#9CA3AF' }}>Avg Score</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-black" style={{ color: '#EF4444' }}>{highStormCount}</div>
              <div className="text-[9px]" style={{ color: '#9CA3AF' }}>High Storm</div>
            </div>
          </div>
        )}
        <Icon
          name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={16}
          style={{ color: '#9CA3AF', flexShrink: 0 }}
        />
      </button>

      {/* Body */}
      {isExpanded && (
        <div className="p-5">
          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="w-10 h-10 rounded-full border-[3px] border-[#E89A3C] border-t-transparent animate-spin" />
              <p className="text-sm font-semibold" style={{ color: '#374151' }}>
                Fetching Census & NOAA data…
              </p>
              <p className="text-xs" style={{ color: '#9CA3AF' }}>
                Querying US Census Bureau ACS + NOAA Storm Events
              </p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
              <Icon name="ExclamationTriangleIcon" size={18} style={{ color: '#EF4444', flexShrink: 0 }} />
              <div>
                <p className="text-sm font-bold" style={{ color: '#991B1B' }}>Data Fetch Error</p>
                <p className="text-xs mt-1" style={{ color: '#B91C1C' }}>{error}</p>
                {error.includes('CENSUS_API_KEY') && (
                  <p className="text-xs mt-2 font-semibold" style={{ color: '#7F1D1D' }}>
                    Add <code className="bg-red-100 px-1 rounded">CENSUS_API_KEY</code> to your .env file.
                    Get a free key at{' '}
                    <a href="https://api.census.gov/data/key_signup.html" target="_blank" rel="noopener noreferrer" className="underline">
                      api.census.gov
                    </a>
                  </p>
                )}
                <button
                  onClick={fetchValidationData}
                  className="mt-3 text-xs font-semibold px-3 py-1.5 rounded-lg"
                  style={{ background: '#EF4444', color: '#fff' }}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          {!loading && !error && data.length > 0 && (
            <div className="space-y-5">
              {/* Overview Chart */}
              <DemandOverviewChart data={data} />

              {/* Split: city list + detail */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* City List */}
                <div
                  className="rounded-xl overflow-hidden"
                  style={{ border: '1px solid #E5E7EB' }}
                >
                  <div
                    className="px-4 py-2.5 flex items-center gap-2"
                    style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}
                  >
                    <Icon name="MapPinIcon" size={12} style={{ color: '#1C2E5E' }} />
                    <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>
                      {data.length} Markets Validated
                    </span>
                  </div>
                  <div className="overflow-y-auto" style={{ maxHeight: 400 }}>
                    {data.map((item) => (
                      <CityValidationRow
                        key={item.label}
                        item={item}
                        isSelected={selectedCity?.label === item.label}
                        onClick={() => setSelectedCity(item)}
                      />
                    ))}
                  </div>
                </div>

                {/* Detail Panel */}
                <div className="overflow-y-auto" style={{ maxHeight: 420 }}>
                  {selectedCity ? (
                    <CityDetailPanel item={selectedCity} />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full py-12 gap-2">
                      <Icon name="CursorArrowRaysIcon" size={32} style={{ color: '#D1D5DB' }} />
                      <p className="text-sm" style={{ color: '#9CA3AF' }}>Select a market to view details</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Validation Legend */}
              <div
                className="rounded-xl p-4"
                style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #DBEAFE' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="InformationCircleIcon" size={14} style={{ color: '#1C2E5E' }} />
                  <span className="text-xs font-bold" style={{ color: '#1C2E5E' }}>
                    How AI Predictions Are Validated
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { status: 'Confirmed', color: '#10B981', desc: 'Census + NOAA data aligns with AI forecast (±10pts)' },
                    { status: 'Overestimated', color: '#EF4444', desc: 'AI predicted higher demand than real data supports' },
                    { status: 'Underestimated', color: '#F59E0B', desc: 'Real data shows stronger demand than AI predicted' },
                    { status: 'Unverified', color: '#9CA3AF', desc: 'No AI prediction available for comparison' },
                  ].map((v) => (
                    <div key={v.status} className="rounded-lg p-2.5" style={{ background: `${v.color}10`, border: `1px solid ${v.color}30` }}>
                      <div className="flex items-center gap-1 mb-1">
                        <div className="w-2 h-2 rounded-full" style={{ background: v.color }} />
                        <span className="text-[10px] font-bold" style={{ color: v.color }}>{v.status}</span>
                      </div>
                      <p className="text-[9px] leading-relaxed" style={{ color: '#6B7280' }}>{v.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Data Sources Footer */}
              <div className="flex items-start gap-2 pt-1">
                <Icon name="CheckBadgeIcon" size={12} style={{ color: '#059669', flexShrink: 0, marginTop: 1 }} />
                <div>
                  <p className="text-[10px] font-semibold" style={{ color: '#065F46' }}>Live Data Sources</p>
                  {sources.map((s, i) => (
                    <p key={i} className="text-[9px] mt-0.5" style={{ color: '#6B7280' }}>• {s}</p>
                  ))}
                  {fetchedAt && (
                    <p className="text-[9px] mt-1" style={{ color: '#9CA3AF' }}>
                      Last fetched: {new Date(fetchedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
